import CategoryBanner, { CategoryBannerProps } from 'components/CategoryBanner/CategoryBanner';
import { GetStaticPaths, GetStaticProps } from 'next';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState, useRef } from 'react';
import LazyHydrate from 'react-lazy-hydration';
import { generateImageW } from 'utils/image.utils';
import { useVTOStore } from 'zus/vtoStore';
import Breadcrumb, { BreadcrumbItem } from 'components/Breadcrumb/Breadcrumb';
import { getLocalCurrency } from 'functions/currency';
import { wcApiDataService } from 'functions/wcApi';
import useWindowSize from 'hooks/useWindowSize';
import { addDataLayer } from 'lib/ga';
import { CategoryFrameList, CategoryLensTypes, FrameShape, GlassesCategory, SeoBlock, SeoMetaTags, CategoryBannerImage } from 'constants/dataTypes';
import { useGlassesStore } from 'zus/glassesStore';
import { getPageHeaderData, getSeoBlockContent, getSeoMetaTags, getCategoryBannerImages } from 'utils/common.utils';
import CategorySeoText from 'components/Others/CategorySeoText';
import FAQJson from 'components/Others/FAQJson';
import GlobalSeoMetaTags from 'components/GlobalSeoMetaTags/GlobalSeoMetaTags';
import React from 'react';
import ProductCardwVTO from 'components/ProductCard/ProductCardwVTO';
import { filterLensTypes } from 'functions/filterLensTypes';
import CategoryShapeSwitcher from 'components/CategoryShapeSwitcher/CategoryShapeSwitcher';
import { frameShapeToName } from 'functions/frameShapeToName';
import { getCategoryTitle } from 'functions/categoryTitle';
import cx from 'classnames';
import CategoryBannerImageComponent from 'components/CategoryBannerImage/CategoryBannerImage';
import { changeSunglasses } from 'functions/changeSunglasses';

const ColorFilter = dynamic(
  () => import('components/ColorFilter/ColorFilter')
);

const VTOModalV2 = dynamic(
  () => import('components/VTO/VTOModalV2'),
  { ssr: false }
);
const RecommendedProductsSlider = dynamic(
  () => import('components/Others/RecommendedProductsSlider')
);

export default function ShopCategory({
  categoryData,
  categoryLensTypes,
  seoBlock,
  seoMetaTags,
  categoryBannerProps,
  pageTitle,
  categoryBannerImages
}: {
  categoryData: GlassesCategory;
  seoBlock: SeoBlock;
  categoryLensTypes: CategoryLensTypes[];
  seoMetaTags: SeoMetaTags;
  categoryBannerProps: CategoryBannerProps;
  pageTitle?: string;
  categoryBannerImages: CategoryBannerImage[];
}) {
  const { width } = useWindowSize(1140);
  const router = useRouter();
  const { category, shape, colour, isUpdate } = router.query;
  const setUpdateGlasses = useGlassesStore((state) => state.setUpdateGlasses);
  const updateData = useGlassesStore((state) => state.updateData);

  const categoryPrice = categoryData.meta.category_price_reference.price as number;
  const categoryRegularPrice = categoryData.meta.category_price_reference.regular_price as number;

  const [selectedSlugs, setSelectedSlugs] = useState<Array<string>>([]);
  const [showVTOModal, setShowVTOModal] = useState(false);

  useEffect(() => {
    const isProductPage = router.pathname.includes('[product]');
    if (!isProductPage && !isUpdate) {
      setUpdateGlasses(null);
    }
  }, [router.pathname, isUpdate]);

  // Handle color changes in update mode
  useEffect(() => {
    if (isUpdate && updateData && colour) {
      // Only update if the color actually changed
      if (updateData.glassesData.frameColor !== colour) {
        const updatedData = {
          ...updateData,
          glassesData: {
            ...updateData.glassesData,
            frameColor: colour as string
          }
        };
        setUpdateGlasses(updatedData);
      }
    }
  }, [colour, isUpdate, updateData]);

  const frameList = useMemo(() => {
    var categoryFrameList: CategoryFrameList[] = [];
    if (categoryData.meta.category_frame_list) {
      categoryFrameList = categoryData.meta.category_frame_list.filter((frame) => {
        const normalizedShape = shape === 'rectangle' ? 'classic' : shape;

        if (shape && frame.frame_shape.toLowerCase() !== normalizedShape) {
          return false;
        }
        if (selectedSlugs.length > 0 && !selectedSlugs.includes(frame.frame_color.slug)) {
          return false;
        }
        return true;
      })
    }

    var categoryProducts: any = [];
    var position = 1;
    categoryFrameList.map((frame: any) => {
      categoryProducts.push({
        name: categoryData.name + ' - ' + frame.frame_color.name,
        price: categoryPrice,
        category: categoryData.name,
        variant: frame.frame_color.name,
        list: 'Category',
        position: position
      });
      position++;
    });
    //setCategoryProductsForPosition(categoryProducts);
    addDataLayer({
      event: 'ImpCompleted',
      ecommerce: {
        currencyCode: getLocalCurrency(router.locale),
        impressions: categoryProducts
      }
    });
    categoryFrameList = categoryFrameList.filter((frame: any) => {
      if (!frame.arms_color || !frame.arms_color.slug) {
        return false;
      }
      return true;
    });
    return categoryFrameList;
  }, [categoryData.meta.category_frame_list, selectedSlugs, shape]);

  useEffect(() => {
    if (frameList.length === 0 && colour && shape) {
      router.push(`/shop/${category}/shape/${shape}`);
    }
  }, [frameList, colour, shape, category, router]);

  const frameShapes = useMemo(() => {
    if (categoryData.meta.category_frame_list) {
      let shapes = Array.from(new Set(categoryData.meta.category_frame_list.map(frame => frame.frame_shape)));
      // Sort with classic first, then alphabetically for remaining shapes
      shapes.sort((a, b) => {
        if (a === 'classic') return -1;
        if (b === 'classic') return 1;
        return a.localeCompare(b);
      });
      return shapes;
    }
    return ['classic', 'round'];
  }, [categoryData.meta.category_frame_list]);

  const colorTitle = useMemo(() => {
    if (!colour) return null;
    return getCategoryTitle({
      query: {
        colour,
        shape: shape as string
      }
    });
  }, [colour, shape]);

  const gridRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (selectedSlugs.length > 0 || shape) {
      gridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [selectedSlugs, shape]);

  const breadcrumbItems = useMemo(() => {
    const items: BreadcrumbItem[] = [
      {
        id: 1,
        href: '',
        text: 'Dresden Vision'
      },
      {
        id: 2,
        href: 'shop',
        text: 'Shop'
      },
      {
        id: 3,
        href: `shop/${changeSunglasses(category as string, { slug: true })}`,
        text: changeSunglasses(categoryData.name, { capitalize: true })
      }
    ];

    // If shape is selected, add shape to breadcrumb
    if (shape) {
      items.push({
        id: 4,
        href: `shop/${changeSunglasses(category as string, { slug: true })}/shape/${shape}`,
        text: `${(shape as string).charAt(0).toUpperCase() + (shape as string).slice(1)} ${changeSunglasses(categoryData.name, { capitalize: true })}`
      });

      // Only add color to breadcrumb if exactly one color is selected
      if (colour && !Array.isArray(colour)) {
        const cleanColorTitle = colorTitle?.replace('Original', '').replace('Capote', '').trim();
        items.push({
          id: 5,
          href: `shop/${changeSunglasses(category as string, { slug: true })}/shape/${shape}/colour/${colour}`,
          text: `${(shape as string).charAt(0).toUpperCase() + (shape as string).slice(1)} ${changeSunglasses(categoryData.name, { capitalize: true })} - ${cleanColorTitle} Frames`
        });
      }
    }
    // If only color is selected (no shape)
    else if (colour && !Array.isArray(colour)) {
      const cleanColorTitle = colorTitle?.replace('Original', '').replace('Capote', '').trim();
      items.push({
        id: 4,
        href: `shop/${changeSunglasses(category as string, { slug: true })}/colour/${colour}`,
        text: `${changeSunglasses(categoryData.name, { capitalize: true })} - ${cleanColorTitle} Frames`
      });
    }

    return items;
  }, [category, shape, colour, colorTitle, categoryData.name]);

  // Update the banner title logic as well
  const bannerTitle = useMemo(() => {
    let title = categoryData.name;

    if (shape) {
      const shapeCapitalized = frameShapeToName(shape as string);
      title += ` - ${shapeCapitalized} Frames`;
    }

    // Only add color to title if exactly one color is selected
    if (colour && !Array.isArray(colour) && colorTitle) {
      title += ` - ${colorTitle}`;
    }

    return title;
  }, [categoryData.name, shape, colour, colorTitle]);

  // Create an indexed map of banners
  const indexedBanners = useMemo(() => {
    const bannerMap = new Map<number, CategoryBannerImage>();

    categoryBannerImages.forEach(banner => {
      if (banner.bannerType === 'small-square') {
        bannerMap.set(banner.orderIndex, banner);
      } else {
        // For large rectangles, calculate the next multiple of 3
        const nextMultipleOfThree = Math.ceil(banner.orderIndex / 3) * 3 + 1;
        bannerMap.set(nextMultipleOfThree, banner);
      }
    });

    return bannerMap;
  }, [categoryBannerImages]);

  // Track the number of category banner images
  let categoryBannerImageCount = 0;

  return (
    <div>
      <Head>
        <title>{`${changeSunglasses(bannerTitle, { capitalize: true })} | Dresden Vision`}</title>
        <meta
          name="twitter:title"
          content={`${changeSunglasses(bannerTitle, { capitalize: true })} | Dresden Vision`}
        />
        <meta
          name="og:title"
          content={`${changeSunglasses(bannerTitle, { capitalize: true })} | Dresden Vision`}
        />
        <meta
          name="description"
          content={`${changeSunglasses(categoryData.name, { capitalize: true })} models with affordable prices and its wide selection are on this page. Start reviewing ${changeSunglasses(categoryData.name, { capitalize: true })} in various color options.`}
        />
        <meta
          property="og:description"
          content={`${changeSunglasses(categoryData.name, { capitalize: true })} models with affordable prices and its wide selection are on this page. Start reviewing ${changeSunglasses(categoryData.name, { capitalize: true })} in various color options.`}
        />
        <meta
          name="twitter:description"
          content={`${changeSunglasses(categoryData.name, { capitalize: true })} models with affordable prices and its wide selection are on this page. Start reviewing ${changeSunglasses(categoryData.name, { capitalize: true })} in various color options.`}
        />
        {/* <meta httpEquiv="Content-Security-Policy" content="default-src * self blob: data: gap:; style-src * self 'unsafe-inline' blob: data: gap:; script-src * 'self' 'unsafe-eval' 'unsafe-inline' blob: data: gap:; object-src * 'self' blob: data: gap:; img-src * self 'unsafe-inline' blob: data: gap:; connect-src self * 'unsafe-inline' blob: data: gap:; frame-src * self blob: data: gap:;" /> */}
      </Head>
      <CategoryBanner
        {...categoryBannerProps}
        title={bannerTitle}
        breadcrumbItems={breadcrumbItems}
      >
        {/* <div className="w-full flex items-center gap-4 max-w-[1224px] mx-auto mb-6 justify-center">
          <div className='bg-[#E8F7F0] flex items-center gap-2 px-4 py-2 py-[10px] px-[18px] rounded-lg'>
            <div className="bg-[#00BE82] text-white px-4 flex items-center justify-center rounded-full font-medium whitespace-nowrap text-[16px] h-[30px]">
              <p className='mt-[-4px]'>Limited-Time Offer</p>
            </div>
            <p className="text-[#2A2A2A] text-[16px]">
              <span className="font-semibold">Bundle & Save 40%</span> on all additional pairs of prescription glasses
            </p>
          </div>
        </div> */}

        <div className={cx("flex flex-col md:flex-row md:justify-end items-center space-y-4 md:space-y-0 md:space-x-4", {
          "mt-5": shape
        })}>
          <div className="flex flex-col md:flex-row items-center space-y-4 md:space-y-0 md:space-x-4 w-full md:w-auto">
            <CategoryShapeSwitcher frameShapes={frameShapes} />
          </div>
          <div className="flex flex-col md:flex-row items-center md:items-start ">
            <ColorFilter
              router={router}
              setSelectedSlugs={setSelectedSlugs}
              categoryFrameList={categoryData.meta.category_frame_list || []}
            />
          </div>
        </div>
      </CategoryBanner>
      <div className="mx-auto max-w-[1224px] bg-white px-4 pt-4">
        <div
          ref={gridRef}
          className="mt-[24px] grid grid-cols-1 gap-y-[20px] md:grid-cols-2 md:gap-x-[15px] md:gap-y-[30px] shop:grid-cols-3"
        >
          {frameList.map((frame, index) => {
            let currentPosition = index + 1 + categoryBannerImageCount;

            const bannerImage = indexedBanners.get(currentPosition);
            if (bannerImage) {
              if (bannerImage.bannerType === 'large-rectangle') {
                categoryBannerImageCount += 3;
              } else {
                categoryBannerImageCount++;
              }
            }
            return (
              <React.Fragment key={`color-${frame.frame_color.slug}-${index}`}>
                {bannerImage && (
                  <div className={bannerImage.bannerType === 'large-rectangle' ? 'col-span-full' : undefined}>
                    <CategoryBannerImageComponent banner={bannerImage} width={width} />
                  </div>
                )}
                <ProductCardwVTO
                  onClickProduct={() => {
                    addDataLayer({
                      event: 'productClick',
                      ecommerce: {
                        click: {
                          actionField: { list: 'category' },
                          products: [
                            {
                              name: categoryData.name + ' - ' + frame.frame_color.name,
                              price: categoryPrice,
                              category: categoryData.name,
                              variant: frame.frame_color.name,
                              list: 'Category',
                              position: index
                            }
                          ]
                        }
                      }
                    });
                  }}
                  width={width}
                  categoryLensTypes={categoryLensTypes}
                  data={{
                    title: `${frame.frame_color.name}`,
                    categorySlug: categoryData.slug,
                    colorSlug: frame.frame_color.slug,
                    armColorSlug: frame.arms_color.slug,
                    price: categoryPrice,
                    regularPrice: categoryRegularPrice,
                    href: `/shop/${changeSunglasses(categoryData.slug, { slug: true })}/${frame.frame_color.slug}`,
                    shape: frame.frame_shape as FrameShape,
                    isPriority: width > 600 ? index <= 1 : index == 0
                  }}
                  onVTOToggle={() => setShowVTOModal(true)}
                />
              </React.Fragment>
            );
          })}
        </div>
        {showVTOModal && (
          <VTOModalV2
            title={categoryData.name}
            category={router.query.category as string}
            categoryLensTypes={categoryLensTypes}
          />
        )}
        <LazyHydrate whenVisible>
          <RecommendedProductsSlider />
        </LazyHydrate>
        <LazyHydrate whenVisible>
          <CategorySeoText seoBlock={seoBlock} />
        </LazyHydrate>
        <FAQJson faq={seoBlock.faq} />
        <GlobalSeoMetaTags seoMetaTags={seoMetaTags} />
      </div>
    </div>
  );
}

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  const paths: any = [];

  if (process.env.NEXT_PUBLIC_ENV !== 'production') {
    return {
      paths: [],
      fallback: 'blocking',
    }
  }
  const categoryOptions = ['prescription-glasses',
    'blue-light-filter-glasses',
    'non-prescription-sunglasses',
    'prescription-sunglasses'];
  if (locales) {
    for (const locale of locales) {
      for (const categoryOption of categoryOptions) {
        paths.push({
          params: { category: changeSunglasses(categoryOption, { slug: true }) },
          locale
        });
      }
    }
  }

  return {
    paths,
    fallback: 'blocking'
  };
};

export const getStaticProps: GetStaticProps = async ({ params, locale }) => {
  const startTime = performance.now();
  let category = params?.category;

  const allowedCategories = ['prescription-glasses', 'blue-light-filter-glasses', 'sunglasses', 'prescription-sunglasses', 'reading-glasses', 'frames'];
  if (!allowedCategories.includes(category as string)) {
    return {
      notFound: true
    };
  }

  // Convert back to original category if needed for API call
  if (changeSunglasses(category as string, { slug: true }) === 'sunglasses') {
    category = 'non-prescription-sunglasses';
  }

  var categoryData: GlassesCategory = await wcApiDataService('cat/' + category, '', locale);

  //console.log('categoryData', categoryData);

  var productDetailPageData: any = await wcApiDataService('glasses/' + category, '', locale);

  const glassesProductData = productDetailPageData?.components;
  const categoryLensTypes: CategoryLensTypes[] = filterLensTypes(glassesProductData);

  if (!categoryData) {
    console.log('categoryData empty for shop/[category]')
    return {
      notFound: true
    };
  }
  const endTime = performance.now();
  //console.log(`Time taken to load category data: ${endTime - startTime} milliseconds`);




  const breadcrumbItemsData: BreadcrumbItem[] = [
    {
      id: 1,
      href: '',
      text: 'Dresden Vision'
    },
    {
      id: 2,
      href: 'shop',
      text: 'Shop'
    },
    {
      id: 3,
      href: `shop/${changeSunglasses(category as string, { slug: true })}`,
      text: changeSunglasses(categoryData.name, { capitalize: true }),
      forceLink: params?.colour != null && typeof params?.colour === 'object' ? true : false
    }
  ];

  if (params?.shape) {
    const shapeCapitalized = frameShapeToName(params?.shape as string);
    breadcrumbItemsData.push({
      id: 4,
      href: `shop/${changeSunglasses(category as string, { slug: true })}/shape/${params?.shape}`,
      text: `Frame Shape: ${shapeCapitalized}`
    });
  }

  const seoMetaTags = await getSeoMetaTags('/shop/' + category, locale);
  const categoryBannerImages = await getCategoryBannerImages(category as string, locale);
  const seoBlock = await getSeoBlockContent('/shop/' + category as string, locale);

  const imageAltTexts: any = {
    au: "Two women are smiling and taking selfies while wearing stylish prescription glasses in Australia.",
    ca: "Two women happily take selfies, wearing stylish prescription glasses in Canada. One wears light green frames, and the other has gray frames, showcasing the fashionable eyewear options available.",
    nz: "Two women are taking selfies while wearing prescription glasses in New Zealand. One has light skin and blond hair, wearing gray glasses, and the other has dark skin and black hair tied back, wearing green glasses. Both are smiling broadly, with the woman in blue glasses holding the camera.",
    international: "Two friends, both wearing prescription glasses, are taking a selfie together. The woman on the right smiles broadly, showing her teeth, while the woman on the left has a gentle smile. Both appear to be enjoying a happy moment outdoors."
  };

  const imageAltText = (categoryData.name === 'Prescription Glasses') ? imageAltTexts[locale as string] || imageAltTexts['international'] : categoryData.name.replace(/(<([^>]+)>)/gi, " ");

  const categoryBannerProps: CategoryBannerProps = {
    title: categoryData.name,
    description: categoryData.description,
    imageSrc: generateImageW(categoryData.slug, 'categories', 'slug', 'banner', 'jpg'),
    imageAlt: imageAltText,
    breadcrumbItems: breadcrumbItemsData
  }

  const notionPageHeaders = await getPageHeaderData(category as string, locale, categoryBannerProps);

  return {
    props: {
      categoryData: categoryData,
      seoBlock,
      seoMetaTags,
      categoryLensTypes,
      categoryBannerProps: notionPageHeaders,
      categoryBannerImages
    }
  };
};
